/**
 * Core API - Profiles
 * 
 * Database operations for user profiles.
 * Profiles are linked to auth.users and contain user metadata.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  employee_id: string | null;
  department: string | null;
  job_title: string | null;
  is_super_admin: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches the current user's profile by their auth ID.
 */
export async function fetchProfileById(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return data as Profile | null;
}

/**
 * Fetches basic profile info (name, email) for display purposes.
 */
export async function fetchProfileBasicInfo(userId: string): Promise<{ full_name: string | null; email: string } | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('full_name, email')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Updates a user's profile.
 */
export async function updateProfile(
  userId: string,
  updates: Partial<Omit<Profile, 'id' | 'email' | 'is_super_admin' | 'created_at' | 'updated_at'>>
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('profiles')
    .update(updates)
    .eq('id', userId);

  if (error) throw error;
}
